import React, { FC, useCallback, useEffect, useState } from 'react';
import { Animal } from '../../types/interfaces/animal.interfaces';
import { InventorySource } from '../../types/enums';
import { getAnimalById } from '../../api/animalsApi';
import { getOnFarmAnimalById } from '../../services/onFarmInventory.service';
import { getAnimalAttachmentURL } from '../../services/animal.service';
import { resolveOnFarmAttachments } from '../../api/onFarmAnimalsApi';
import { showToast } from '../../services/toast.service';
import { oneBitImage } from '../../utils/commonUtils';
import { ANIMAL_CONSTANTS, LABEL_CONSTANTS, MARKETPLACE_CONSTANTS } from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';

type AnimalBannerProps = {
  animalId: string | undefined;
  animal?: Animal;
  children: React.ReactNode | React.ReactNode[];
  onAnimalLoad?: (animal: Animal) => void;
  inventorySource?: InventorySource;
  headerAction?: React.ReactNode;
  embryoStraw?: {
    embryoStrawCode?: string;
    embryoStage?: string;
    embryoGrade?: string;
  };
};

const AnimalBanner: FC<AnimalBannerProps> = ({
  animalId,
  animal: initialAnimal,
  children,
  embryoStraw,
  onAnimalLoad,
  inventorySource = InventorySource.RSG,
  headerAction,
}): JSX.Element => {
  const [animal, setAnimal] = useState<Animal | undefined>(initialAnimal);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<number | null>(null);

  const resolveAttachmentURL = (attachmentId: number, accessToken: string) =>
    inventorySource === InventorySource.ON_FARM ? accessToken : getAnimalAttachmentURL(animalId!, attachmentId, accessToken);

  const attachments = animal?.attachments ?? [];
  const selectedAttachment = attachments.find(attachment => attachment.attachmentId === selectedAttachmentId) ?? attachments[0];

  useEffect(() => {
    if (!attachments.length) {
      setSelectedAttachmentId(null);
      return;
    }
    if (!attachments.some(attachment => attachment.attachmentId === selectedAttachmentId)) {
      setSelectedAttachmentId(attachments[0].attachmentId);
    }
  }, [attachments, selectedAttachmentId]);

  const fetchAnimal = useCallback(async () => {
    if (!animalId) return;

    try {
      const params = { include: 'Breed.Species, Attachments' };
      const response =
        inventorySource === InventorySource.ON_FARM
          ? await getOnFarmAnimalById(animalId, params)
          : await getAnimalById(animalId, params);

      const animalData =
        inventorySource === InventorySource.ON_FARM
          ? { ...response.data, attachments: await resolveOnFarmAttachments(animalId, response.data.attachments) }
          : response.data;

      setAnimal(animalData);
      onAnimalLoad?.(animalData);
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, [animalId, inventorySource, onAnimalLoad]);

  useEffect(() => {
    if (initialAnimal) {
      setAnimal(initialAnimal);
      return;
    }
    fetchAnimal();
  }, [initialAnimal, fetchAnimal]);

  return (
    <div className="card animal-info">
      <div className="animal-header-info">
        <div className="name">
          {headerAction ? (
            <div className="name-primary">
              <h2>{animal?.name}</h2>
              {headerAction}
            </div>
          ) : (
            <h2>{animal?.name}</h2>
          )}
          <div>
            {ANIMAL_CONSTANTS.ANIMAL_CODE}: {animal?.code}
          </div>
        </div>
        <hr />
      </div>
      <div className="animal-banner">
        {animal && attachments.length > 0 ? (
          <div className="animal-photos">
            <img
              src={
                animalId && selectedAttachment
                  ? resolveAttachmentURL(selectedAttachment.attachmentId, selectedAttachment.accessToken)
                  : oneBitImage
              }
              alt="Animal Photos"
            />
            {attachments.length > 1 && (
              <div className="animal-photos-row">
                {attachments.map((attachment, index) => (
                  <button
                    key={attachment.attachmentId}
                    type="button"
                    className={`photo-thumbnail${
                      selectedAttachment?.attachmentId === attachment.attachmentId ? ' selected' : ''
                    }`}
                    onClick={() => setSelectedAttachmentId(attachment.attachmentId)}>
                    <img
                      src={resolveAttachmentURL(attachment.attachmentId, attachment.accessToken)}
                      alt={`Animal's Photo ${index + 1}`}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="specimen-image">{ANIMAL_CONSTANTS.NO_IMAGE}</div>
        )}
        <div className="specimen-info">
          <div className="stats">
            <div>
              <h3>{ANIMAL_CONSTANTS.DOB}:</h3>
              <div>{animal?.birthDate ? new Date(animal?.birthDate).toLocaleDateString() : 'Unknown'}</div>
              <br />
              <h3>{ANIMAL_CONSTANTS.DAM}:</h3>
              <div>{animal?.dam}</div>
            </div>
            <div>
              <h3>{ANIMAL_CONSTANTS.GENDER}</h3>
              <div>{animal?.gender}</div>
              <br />
              <h3>{ANIMAL_CONSTANTS.DNA}:</h3>
              <div>{animal?.dna}</div>
            </div>
            <div>
              <h3>{ANIMAL_CONSTANTS.BREED}:</h3>
              <div>{animal?.breed?.name}</div>
              <br />
              <h3>{ANIMAL_CONSTANTS.REGISTRATION}:</h3>
              <div>{animal?.registration}</div>
            </div>
            <div>
              <h3>{ANIMAL_CONSTANTS.SIRE}:</h3>
              <div>{animal?.sire}</div>
              <br />
              <h3>{ANIMAL_CONSTANTS.SPECIES}:</h3>
              <div>{animal?.breed?.species?.name}</div>
            </div>
            {inventorySource === InventorySource.ON_FARM && (
              <>
                <div>
                  <h3>{ANIMAL_CONSTANTS.EAR_TAG}:</h3>
                  <div>{animal?.earTag}</div>
                  <br />
                  <h3>{ANIMAL_CONSTANTS.SCRAPIE_TAG}:</h3>
                  <div>{animal?.scrapieTag}</div>
                </div>
                <div>
                  <h3>{ANIMAL_CONSTANTS.RFID_EID}:</h3>
                  <div>{animal?.rfidEid}</div>
                </div>
              </>
            )}
          </div>
          {embryoStraw && (
            <div className="stats embryo-straw-stats">
              <div>
                <h3>{LABEL_CONSTANTS.STRAW_LOT}:</h3>
                <div>{embryoStraw.embryoStrawCode || MARKETPLACE_CONSTANTS.EMPTY_VALUE}</div>
              </div>
              <div>
                <h3>{LABEL_CONSTANTS.EMBRYO_STAGE}:</h3>
                <div>{embryoStraw.embryoStage || MARKETPLACE_CONSTANTS.EMPTY_VALUE}</div>
              </div>
              <div>
                <h3>{LABEL_CONSTANTS.EMBRYO_GRADE}:</h3>
                <div>{embryoStraw.embryoGrade || MARKETPLACE_CONSTANTS.EMPTY_VALUE}</div>
              </div>
            </div>
          )}

          <div className="description">
            <h3>{ANIMAL_CONSTANTS.DESCRIPTION}: &nbsp; </h3>
            <div>{animal?.description}</div>
          </div>

          {children}
        </div>
      </div>
    </div>
  );
};

export default AnimalBanner;
